"use client";

/**
 * 상품 상세 TanStack Query 훅
 *
 * features 레이어: shared, ui, lib, hooks, workspace-pkg import 가능
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createCartService, type AddToCartRequest } from "@/shared/api/services/cartService";
import { createProductService, type FavoriteListItem } from "@/shared/api/services/productService";
import { useAuth, useBuyerService } from "@/shared/auth/hooks";

import { cartKeys } from "@/features/cart/queries/keys";
import { favoriteKeys, productDetailKeys } from "@/features/shopping/queries/keys";

export { productDetailKeys };
export type { FavoriteListItem };

/**
 * 상품 상세 조회 훅
 */
export function useProductDetail(id: string) {
  const service = useBuyerService(createProductService);

  return useQuery({
    queryKey: productDetailKeys.detail(id),
    queryFn: () => service.getProductDetail(id),
    enabled: !!id,
  });
}

/**
 * 찜 여부 확인 쿼리 훅
 *
 * 백엔드 응답 { code, message, data: boolean } →
 * 클라이언트 래핑 { data: { data: boolean } } → boolean 추출
 */
export function useCheckFavorite(productId: string) {
  const { isAuthenticated } = useAuth();
  const service = useBuyerService(createProductService);

  return useQuery({
    queryKey: favoriteKeys.check(productId),
    queryFn: async () => {
      const res = await service.checkFavorite(productId);
      return res.data;
    },
    enabled: !!productId && isAuthenticated,
  });
}

/**
 * 즐겨찾기 토글 뮤테이션 훅
 */
export function useToggleFavoriteDetail() {
  const queryClient = useQueryClient();
  const service = useBuyerService(createProductService);

  return useMutation({
    mutationFn: (productId: number) => service.toggleFavorite(productId),
    onSuccess: (_data, productId) => {
      void queryClient.invalidateQueries({ queryKey: favoriteKeys.check(String(productId)) });
      void queryClient.invalidateQueries({ queryKey: favoriteKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productDetailKeys.all });
    },
  });
}

/**
 * 찜 목록 조회 쿼리 훅
 */
export function useFavoriteList() {
  const { isAuthenticated } = useAuth();
  const service = useBuyerService(createProductService);

  return useQuery({
    queryKey: favoriteKeys.list(),
    queryFn: async () => {
      const res = await service.getFavoriteList();
      return res.data;
    },
    enabled: isAuthenticated,
  });
}

/**
 * 장바구니 추가 뮤테이션 훅
 */
export function useAddToCart() {
  const queryClient = useQueryClient();
  const cartService = useBuyerService(createCartService);

  return useMutation({
    mutationFn: (data: AddToCartRequest) => cartService.addToCart(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cartKeys.all });
    },
  });
}
